{
  const starPath =
    'M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.9l-4.94 2.6.94-5.5-4-3.9 5.53-.8L10 1.5z';

  const verifiedPath =
    'M10 1.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17zm3.7 6.3l-4.2 4.2a1 1 0 01-1.4 0L6.3 10.2a1 1 0 011.4-1.4l1.1 1.1 3.5-3.5a1 1 0 011.4 1.4z';

  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const stripHtml = (value) => {
    const node = document.createElement('div');
    node.innerHTML = value || '';
    return (node.textContent || '').trim();
  };

  const upgradeImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    return url
      .replace(/([?&]width=)\d+/i, '$11200')
      .replace(/([?&]quality=)\d+/i, '$190');
  };

  const pictureUrl = (review) => {
    const pictures = review?.pictures_urls;
    if (!Array.isArray(pictures) || !pictures.length) return '';
    const first = pictures[0];
    if (typeof first === 'string') return upgradeImageUrl(first);
    if (first?.original) return first.original;
    if (first?.huge) return first.huge;
    return upgradeImageUrl(first?.compact || first?.small || '');
  };

  const tagsFromAnswers = (review) => {
    const answers = review?.cf_answers;
    if (!Array.isArray(answers) || !answers.length) return '';

    return answers
      .map((answer) => {
        const value = answer?.value ?? answer?.answer ?? answer?.text;
        if (Array.isArray(value)) return value.filter(Boolean).join(', ');
        return value;
      })
      .filter((value) => typeof value === 'string' && value.trim())
      .map((value) => value.trim())
      .join(' | ');
  };

  const normalizeReview = (review, { fromMediaGallery = false } = {}) => {
    if (!review || typeof review !== 'object') return null;

    const body = (review.body || stripHtml(review.body_html) || '').trim();
    if (!body) return null;

    const rating = Number.parseInt(review.rating, 10);
    const image = pictureUrl(review);
    return {
      id: review.uuid || `${review.reviewer_name || 'review'}-${body.slice(0, 24)}`,
      title: (review.title || '').trim(),
      body,
      rating: Number.isFinite(rating) ? Math.min(5, Math.max(1, rating)) : 5,
      author: (review.reviewer_name || 'Anonymous').trim() || 'Anonymous',
      verified: Boolean(review.verified_buyer),
      image,
      hasReviewMedia: Boolean(image),
      fromMediaGallery,
      tags: tagsFromAnswers(review),
      pinned: Boolean(review.pinned),
      expandable: review.expandable === true,
    };
  };

  const mergeReviews = (data) => {
    const byId = new Map();

    const upsert = (item, options) => {
      const review = normalizeReview(item, options);
      if (!review) return;
      const existing = byId.get(review.id);
      if (!existing) {
        byId.set(review.id, review);
        return;
      }
      byId.set(review.id, {
        ...existing,
        ...review,
        image: review.image || existing.image,
        hasReviewMedia: review.hasReviewMedia || existing.hasReviewMedia,
        fromMediaGallery: review.fromMediaGallery || existing.fromMediaGallery,
        tags: review.tags || existing.tags,
        title: review.title || existing.title,
        verified: review.verified || existing.verified,
        expandable: review.expandable || existing.expandable,
      });
    };

    // Media gallery first so photo reviews keep source order.
    if (Array.isArray(data?.photo_gallery)) {
      for (const item of data.photo_gallery) {
        upsert(item, { fromMediaGallery: true });
      }
    }
    if (Array.isArray(data?.reviews)) {
      for (const item of data.reviews) {
        upsert(item);
      }
    }

    return [...byId.values()];
  };

  const pickReviews = (reviews, { max, preferPhotos, fallbackImage }) => {
    const withFallback = reviews.map((review) => ({
      ...review,
      image: review.image || fallbackImage || '',
      usedProductFallback: !review.hasReviewMedia && Boolean(fallbackImage),
    }));

    if (!preferPhotos) {
      return withFallback.slice(0, max);
    }

    const mediaFirst = withFallback.filter((review) => review.hasReviewMedia);
    const remainder = withFallback.filter((review) => !review.hasReviewMedia);

    const rank = (list) =>
      [...list].sort((a, b) => {
        if (a.fromMediaGallery !== b.fromMediaGallery) {
          return a.fromMediaGallery ? -1 : 1;
        }
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        if (a.verified !== b.verified) return a.verified ? -1 : 1;
        if (Boolean(a.title) !== Boolean(b.title)) return a.title ? -1 : 1;
        return b.rating - a.rating;
      });

    return [...rank(mediaFirst), ...rank(remainder)].slice(0, max);
  };

  const starsMarkup = (rating) => {
    let html = '';
    for (let i = 1; i <= 5; i += 1) {
      const empty = i > rating ? ' rg-reviews__star--empty' : '';
      html += `<svg class="rg-reviews__star${empty}" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path fill="currentColor" d="${starPath}"/></svg>`;
    }
    return html;
  };

  const cardMarkup = (review) => {
    const media = review.image
      ? `<figure class="rg-reviews__media${review.usedProductFallback ? ' rg-reviews__media--fallback' : ''}"><img class="rg-reviews__image" src="${escapeHtml(review.image)}" alt="" loading="lazy" width="620" height="352" decoding="async"></figure>`
      : `<div class="rg-reviews__media rg-reviews__media--empty" aria-hidden="true"></div>`;

    const title = review.title
      ? `<h3 class="rg-reviews__title">${escapeHtml(review.title)}</h3>`
      : '';

    const verified = review.verified
      ? `<span class="rg-reviews__verified"><svg class="rg-reviews__verified-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path fill="currentColor" d="${verifiedPath}"/></svg>Verified</span>`
      : '';

    const collapsed = review.expandable ? 'true' : 'false';
    const readMore = review.expandable
      ? '<button type="button" class="rg-reviews__toggle" data-rg-review-toggle>Read more</button>'
      : '';

    return `
      <li class="rg-reviews__item">
        <article class="rg-reviews__card">
          ${media}
          <div class="rg-reviews__stars" role="img" aria-label="${review.rating} out of 5 stars">
            ${starsMarkup(review.rating)}
          </div>
          ${title}
          <div class="rg-reviews__body" data-rg-review-body data-collapsed="${collapsed}">
            <p class="rg-reviews__text">${escapeHtml(review.body)}</p>
            ${readMore}
          </div>
          <div class="rg-reviews__footer">
            <span class="rg-reviews__author">${escapeHtml(review.author)}</span>
            ${verified}
          </div>
        </article>
      </li>
    `;
  };

  const setupReadMore = (body) => {
    const toggle = body.querySelector('[data-rg-review-toggle]');
    if (!toggle) return;

    toggle.addEventListener('click', () => {
      const collapsed = body.dataset.collapsed !== 'false';
      body.dataset.collapsed = collapsed ? 'false' : 'true';
      toggle.textContent = collapsed ? 'Read less' : 'Read more';
    });
  };

  const initRoot = (root) => {
    const jsonNode = root.querySelector('[data-rg-reviews-json]');
    const list = root.querySelector('[data-rg-reviews-list]');
    if (!jsonNode || !list) return;

    let data;
    try {
      data = JSON.parse(jsonNode.textContent);
    } catch {
      return;
    }

    const max = Number.parseInt(root.dataset.maxReviews, 10) || 3;
    const preferPhotos = root.dataset.preferPhotos !== 'false';
    const fallbackImage =
      root.dataset.productImage || upgradeImageUrl(data.image_url) || '';
    const reviews = pickReviews(mergeReviews(data), {
      max,
      preferPhotos,
      fallbackImage,
    });
    if (!reviews.length) return;

    list.innerHTML = reviews.map(cardMarkup).join('');
    root.hidden = false;
    root.querySelectorAll('[data-rg-review-body]').forEach(setupReadMore);
    setupCarousel(root, list, reviews.length);

    const seeAll = root.querySelector('[data-rg-see-all-reviews]');
    if (seeAll) {
      const count =
        root.dataset.metafieldCount ||
        data.number_of_reviews ||
        reviews.length;
      const labelTemplate = root.dataset.seeAllLabel || 'See all [count] reviews';
      seeAll.textContent = labelTemplate.replaceAll('[count]', String(count));
      seeAll.hidden = false;

      seeAll.addEventListener('click', (event) => {
        const href = seeAll.getAttribute('href') || root.dataset.seeAllAnchor || '';
        if (!href.startsWith('#')) return;

        const target =
          document.querySelector(href) ||
          document.querySelector('.jdgm-rev-widg, .jdgm-widget.jdgm-review-widget');
        if (!target) return;

        event.preventDefault();
        if (href === '#MainProductReviews' && !target.id) {
          target.id = 'MainProductReviews';
        }
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const setupCarousel = (root, list, count) => {
    const items = [...list.querySelectorAll('.rg-reviews__item')];
    if (!items.length) return;

    const dots = root.querySelector('[data-rg-reviews-dots]');
    const prev = root.querySelector('[data-rg-reviews-prev]');
    const next = root.querySelector('[data-rg-reviews-next]');

    const nearestIndex = () => {
      const left = list.scrollLeft;
      let best = 0;
      let bestDistance = Infinity;
      items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    };

    const goTo = (index, { smooth = true } = {}) => {
      const normalized = ((index % items.length) + items.length) % items.length;
      const item = items[normalized];
      if (!item) return;
      list.scrollTo({
        left: item.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto',
      });
      setActive(normalized);
    };

    const setActive = (index) => {
      dots?.querySelectorAll('[data-rg-dot]').forEach((dot) => {
        const isActive = Number(dot.dataset.rgDot) === index;
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    if (dots && count >= 2) {
      dots.innerHTML = items
        .map(
          (_, index) =>
            `<button type="button" class="rg-reviews__dot" role="tab" aria-label="Review ${index + 1}" aria-selected="${index === 0 ? 'true' : 'false'}" data-rg-dot="${index}"></button>`
        )
        .join('');
      dots.hidden = false;

      dots.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-rg-dot]');
        if (!dot) return;
        goTo(Number(dot.dataset.rgDot));
      });
    }

    const desktopCarousel = window.matchMedia('(min-width: 750px)');

    const syncArrows = () => {
      const show = count >= 2 && desktopCarousel.matches;
      if (prev) prev.hidden = !show;
      if (next) next.hidden = !show;
    };

    if (count >= 2 && prev && next) {
      syncArrows();
      desktopCarousel.addEventListener('change', syncArrows);
      prev.addEventListener('click', () => goTo(nearestIndex() - 1));
      next.addEventListener('click', () => goTo(nearestIndex() + 1));
    }

    list.addEventListener(
      'scroll',
      () => {
        setActive(nearestIndex());
      },
      { passive: true }
    );
  };

  const boot = () => {
    document.querySelectorAll('[data-rg-reviews]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
